import { FileLine } from '@dropbox/dig-icons/assets';
import DocumentIcon from 'hellospa/components/prep-and-send/stepper/icons/document';
import { keyBy, unreachable } from 'js/sign-components/common/ts-utils';
import { Field } from 'signer-app/types/editor-types';
import csvReducer from './csv-editor/csv-editor-slice';
import { CSV_EDITOR_KEY } from './csv-editor/csv-editor-types';
import {
  Actions,
  PrepAndSendAction,
  PrepAndSendNamespaceState,
  RequestTypes,
} from './types';
import {
  SignerFile,
  UserFile,
  UserFileKeyed,
  UserFilesKeyed,
} from './types/file';
import {
  Attachment,
  Recipient,
  RecipientKeyed,
  recipientTypes,
} from './types/recipient';
import { Workflow, WorkflowComponent } from './types/workflow';

const Placeholder: WorkflowComponent = () => {
  return null;
};
Placeholder.selectFormValues = () => ({});
Placeholder.selectFormSchema = () => null;
Placeholder.getText = () => {
  return {};
};

const makeDefaultWorkflowPlaceholder = (): Workflow => {
  return {
    id: 'placeholder-workflow' as any,
    requestType: RequestTypes.RequestSig,
    tags: [],
    name: 'PLACEHOLDER',
    title: null,
    pages: [
      {
        onSave: [],
        hideFooter: false,
        text: {},
        component: Placeholder,
        icon: DocumentIcon,
        path: 'documents',
        uiIcon: FileLine,
        triggers: {
          load: [],
          submit: [],
          done: [],
        },
      },
    ],
  };
};

export const defaultState: PrepAndSendNamespaceState = {
  contacts: {
    lastUpdated: 0,
    sortedData: [],
  },
  transmissionGroupGuid: '',
  signatureRequestId: '',
  document: { title: '', message: '', expiresAt: null },
  templates: [],
  files: {},
  editorFields: {
    pages: [],
    fields: [],
    pdfFields: [],
    rules: [],
    initialPageRotation: 0,
  },
  editorFeatures: [],
  recipients: [],
  ccs: [],
  coverPage: { from: '', to: '', message: '' },
  user: {
    name: '',
    email: '',
    isPaidAccount: false,
    numDocsLeft: 0,
    accountDateFormat: 'MM / DD / YYYY',
    isConfirmedAccount: false,
  },
  apiApp: undefined,
  flags: {
    requestType: RequestTypes.RequestSig,
    allowRecipientReassignment: true,
    limitRecipientCount: false,
    maxRecipients: 20,
    hideProgressStepper: false,
    hideStepperContainer: false,
    alwaysSendMeACopy: true,
    alwaysSendOthersACopy: true,
    uploadIntegrations: {},
    smsAuthLeft: 0,
    needsCharge: false,
    chargeType: 'charge_alert',
    totalPriceInCents: 0,
    freeFaxPagesLeft: 0,
    faxPageCurrentCount: 0,
    overageFaxMaxLimitInCents: 0,
    pricePerFaxInCents: 0,
    initialPageLimit: 10,
    overagePriceInCentsPerPage: 0,
    internationalMultiplier: 1,
    allowTemplateDelete: true,
    allowCreateEditTemplate: true,
    allowTemplatePreview: false,
    allowOptionalTemplateSigners: false,
  },
  settings: {
    recipientReassignment: true,
    sendHighResFax: true,
    hasForm: false,
  },
  meta: {
    currentPath: '',
    nextRecipientId: 1,
    embeddedDocumentWasUpdated: false,
    embeddedRecipientsWereUpdated: false,
  },
  workflow: makeDefaultWorkflowPlaceholder(),
  bulkSendData: {
    exampleFields: [],
    signerFile: null,
    dataKey: '',
  },
  integrationError: undefined,
  embeddedData: undefined,
  numFields: 0,
  requestIsFinished: false,
  phoneVerificationModalOpen: false,
} as Omit<
  PrepAndSendNamespaceState,
  typeof CSV_EDITOR_KEY
> as PrepAndSendNamespaceState;

function updateOrAddItem<T extends object, K extends keyof T>(
  list: readonly T[],
  key: K,
  id: T[K],
  update: (t?: T) => T,
) {
  const index = list.findIndex((t: T) => t[key] === id);
  const currentValue = index >= 0 ? list[index] : undefined;

  const nextValue = update(currentValue);

  const clone = [...list];
  if (index >= 0) {
    clone[index] = nextValue;
  } else {
    clone.push(nextValue);
  }
  return clone;
}

function updateItem<T extends object, K extends keyof T>(
  list: readonly T[],
  key: K,
  id: T[K],
  update: (t: T) => T,
) {
  return updateOrAddItem(list, key, id, (current) => {
    if (current != null) {
      return update(current);
    }
    throw new Error(`Unable to update item ${id}`);
  });
}

function recipientReducer(
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState {
  const convertToKeyed = (recipient: Recipient): RecipientKeyed => {
    if ('attachments' in recipient) {
      recipient.attachments.reduce(
        (acc, attachment) => {
          acc[attachment.id] = attachment;
          return acc;
        },
        {} as Record<string, Attachment>,
      );
    }

    return recipient as RecipientKeyed;
  };

  switch (action.type) {
    case Actions.ResetRecipients: {
      let { recipients } = action.payload;

      // Find the next ID based on whatever has already been assigned and ID
      const assignedIds = recipients
        .map((r) => Number(r.id))
        // auto-generated recipients have a UUID until they're saved
        .filter((n) => !isNaN(n));

      let nextRecipientId = 1;

      // Now assign the next ID to any auto-generated recipients
      recipients = recipients.map((recipient) => {
        if (isNaN(Number(recipient.id))) {
          // Find the next unused ID
          while (assignedIds.includes(nextRecipientId)) {
            nextRecipientId++;
          }

          assignedIds.push(nextRecipientId);
          const id = String(nextRecipientId++);
          return { ...recipient, id };
        }
        return recipient;
      });

      nextRecipientId = 1 + Math.max(0, ...assignedIds);
      return {
        ...state,
        meta: {
          ...state.meta,
          nextRecipientId,
          embeddedRecipientsWereUpdated:
            action.meta.embeddedRecipientsWereUpdated || false,
        },
        recipients: recipients.map(convertToKeyed),
      };
    }
    case Actions.CreateRecipient: {
      const recipient = action.payload;

      let id = Number(recipient.id);
      if (Number.isNaN(id)) {
        id = state.meta.nextRecipientId;
      }
      let nextRecipientId = state.meta.nextRecipientId;
      if (id >= nextRecipientId) {
        nextRecipientId = id + 1;
      }

      return {
        ...state,
        meta: {
          ...state.meta,
          nextRecipientId,
        },
        recipients: updateOrAddItem(state.recipients, 'id', String(id), () =>
          convertToKeyed(recipient),
        ),
      };
    }
    case Actions.UpdateRecipient: {
      const { id, updates } = action.payload;

      return {
        ...state,
        recipients: updateItem(state.recipients, 'id', id, (recipient) => {
          return Object.assign({}, recipient, updates);
        }),
      };
    }
    case Actions.DeleteRecipient: {
      const recipients = state.recipients.filter(
        (recipient) => recipient.id !== action.payload,
      );

      return {
        ...state,
        recipients,
      };
    }
    case Actions.CreateAttachment: {
      const { signerId, attachment } = action.payload;
      const index = state.recipients.findIndex(
        (recipient) => recipient.id === signerId,
      );
      let signer = index >= 0 ? state.recipients[index] : undefined;
      if (
        !signer ||
        (signer.type !== recipientTypes.Signer &&
          signer.type !== recipientTypes.Role)
      ) {
        throw new Error(`Invalid signer ID: ${signerId}`);
      }

      signer = {
        ...signer,
        attachments: {
          ...signer.attachments,
          [attachment.id]: attachment,
        },
      };
      const recipients = [...state.recipients];
      recipients[index] = signer;

      return {
        ...state,
        recipients,
      };
    }
    case Actions.UpdateAttachment: {
      const { signerId, id, updates } = action.payload;
      const index = state.recipients.findIndex(
        (recipient) => recipient.id === signerId,
      );
      let signer = index >= 0 ? state.recipients[index] : undefined;
      if (
        !signer ||
        (signer.type !== recipientTypes.Signer &&
          signer.type !== recipientTypes.Role)
      ) {
        throw new Error(`Invalid signer ID: ${signerId}`);
      }
      let attachment = signer.attachments[id];
      if (!attachment) {
        throw new Error(`Missing attachment: ${id}`);
      }
      attachment = {
        ...attachment,
        ...updates,
      };

      signer = {
        ...signer,
        attachments: {
          ...signer.attachments,
          [attachment.id]: attachment,
        },
      };

      const recipients = [...state.recipients];
      recipients[index] = signer;

      return {
        ...state,
        recipients,
      };
    }
    case Actions.DeleteAttachment: {
      const { signerId, id } = action.payload;
      const index = state.recipients.findIndex(
        (recipient) => recipient.id === signerId,
      );
      let signer = index >= 0 ? state.recipients[index] : undefined;
      if (
        !signer ||
        (signer.type !== recipientTypes.Signer &&
          signer.type !== recipientTypes.Role)
      ) {
        throw new Error(`Invalid signer ID: ${signerId}`);
      }
      const attachment = signer.attachments[id];
      if (!attachment) {
        throw new Error(`Missing attachment: ${id}`);
      }

      const { [id]: remove, ...attachments } = signer.attachments;

      signer = { ...signer, attachments };

      const recipients = [...state.recipients];
      recipients[index] = signer;

      return {
        ...state,
        recipients,
      };
    }
    default:
      return state;
  }
}

const ccReducer = (
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState => {
  switch (action.type) {
    case Actions.SetCCs:
      return {
        ...state,
        ccs: action.payload,
      };
    case Actions.CreateCC:
      return {
        ...state,
        ccs: updateOrAddItem(
          state.ccs,
          'id',
          action.payload.id,
          () => action.payload,
        ),
      };
    case Actions.UpdateCC:
      return {
        ...state,
        ccs: updateItem(state.ccs, 'id', action.payload.id, (cc) => {
          // I had to use assign instead of the spread operator because
          // updates is a Partial<CC> and cc is a CC, but the spread operator
          // complains because they might be a mix of CCEmail and CCRole.
          return Object.assign({}, cc, action.payload.updates);
        }),
      };
    case Actions.DeleteCC: {
      const ccs = state.ccs.filter((cc) => cc.id !== action.payload);
      return {
        ...state,
        ccs,
      };
    }
    default:
      return state;
  }
};

const documentReducer = (
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState => {
  switch (action.type) {
    case Actions.UpdateDocument: {
      return {
        ...state,
        document: {
          ...state.document,
          ...action.payload,
        },
        meta: {
          ...state.meta,
          embeddedDocumentWasUpdated: action.meta.embeddedDocumentWasUpdated,
        },
      };
    }
    default:
      return state;
  }
};

function filesReducer(
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState {
  const convertToKeyed = (file: UserFile): UserFileKeyed => {
    return {
      ...file,
      fields: keyBy('id', file.fields),
    };
  };
  const defaultFile = {
    status: 'converting',
  };

  switch (action.type) {
    case Actions.CreateFile: {
      const file = action.payload;
      return {
        ...state,
        files: {
          ...state.files,
          [file.rootSnapshotGuid]: {
            ...defaultFile,
            ...convertToKeyed(file),
          },
        },
      };
    }
    case Actions.UpdateFile:
    case Actions.ConvertFile: {
      const file = action.payload;
      const files = state.files;

      // if file doesn't exist, don't update file, return current state
      if (!Object.keys(files).includes(file.rootSnapshotGuid)) {
        return state;
      }

      return {
        ...state,
        files: {
          ...state.files,
          [file.rootSnapshotGuid]: {
            ...defaultFile,
            ...convertToKeyed(file),
          },
        },
      };
    }
    case Actions.UpdateField: {
      const mergeField = action.payload;
      const files = { ...state.files };

      /**
       * editorFields don't have a property to say which file they're in, so
       * this needs to gather the field IDs to apply the right updates.
       */
      const fieldUpdates: Record<Field['id'], Partial<Field>> = {};

      mergeField.fileIds.forEach((fileId) => {
        const file = files[fileId];
        // file and file.fields could be `undefined`
        if (file && file.fields) {
          const fields = Object.entries(file.fields).map(([key, field]) => {
            if (
              field &&
              field.signer === 'sender' &&
              field.name === mergeField.field.name
            ) {
              if (
                field.type === 'checkbox' &&
                mergeField.field.type === 'checkbox'
              ) {
                fieldUpdates[field.id] = {
                  checked: mergeField.field.checked,
                };
                return [
                  key,
                  {
                    ...field,
                    checked: mergeField.field.checked,
                  },
                ];
              }
              if (field.type === 'text' && mergeField.field.type === 'text') {
                const mergeData: Partial<Field> = {
                  value: mergeField.field.value,
                  lines: mergeField.field.lines,
                };
                if (mergeField.field.fontSize) {
                  mergeData.fontSize = mergeField.field.fontSize;
                }
                fieldUpdates[field.id] = mergeData;

                return [
                  key,
                  {
                    ...field,
                    ...mergeData,
                  },
                ];
              }
            }
            return [key, field];
          });
          files[fileId] = {
            ...file,
            fields: Object.fromEntries(fields),
          };
        }
      });

      const editorFields: Field[] = state.editorFields.fields.map((field) => {
        if (fieldUpdates[field.id]) {
          return {
            ...field,
            ...fieldUpdates[field.id],
            // TS isn't convinced that field and fieldUpdates[field.id] are the
            // same type. There's nothing here that can prove they aren't
            // mismatched with one being a Text and the other being a Checkbox.
          } as Field;
        }
        return field;
      });

      return {
        ...state,
        files,
        editorFields: {
          ...state.editorFields,
          fields: editorFields,
        },
      };
    }
    case Actions.DeleteFile: {
      const id = action.payload;
      const { [id]: remove, ...files } = state.files;

      return {
        ...state,
        files,
      };
    }
    case Actions.ReorderFile: {
      const { id, oldOrder, newOrder } = action.payload;
      const files = state.files;
      const fileIds = Object.keys(files);

      // current file => order map
      const mappedFiles = fileIds.reduce(
        (mappedOrder, f) => {
          const file = files[f] as UserFileKeyed;
          mappedOrder[f] = file.order;
          return mappedOrder;
        },
        {} as { [key: string]: number },
      );
      const flattenedIndex = Object.values(mappedFiles).sort((a, b) => a - b);

      const updatedMappedFiles = {} as { [key: string]: number };
      if (newOrder < oldOrder) {
        // dragging upwards, e.g. newOrder: 0, oldOrder: 2
        for (let i = newOrder; i < oldOrder; i++) {
          const fileId = fileIds.findIndex((f) => mappedFiles[f] === i);

          // get current index, so we can get the next value
          const currentOrderIndex = flattenedIndex.findIndex((num) => {
            return mappedFiles[fileIds[fileId]] === num;
          });
          updatedMappedFiles[fileIds[fileId]] =
            flattenedIndex[currentOrderIndex + 1];
        }
        updatedMappedFiles[id] = newOrder;
      } else {
        // dragging downwards, e.g. newOrder: 2, oldOrder: 0
        for (let i = newOrder; i > oldOrder; i--) {
          const fileId = fileIds.findIndex((f) => mappedFiles[f] === i);

          // get current index, so we can get the previous value
          const currentOrderIndex = flattenedIndex.findIndex((num) => {
            return mappedFiles[fileIds[fileId]] === num;
          });
          updatedMappedFiles[fileIds[fileId]] =
            flattenedIndex[currentOrderIndex - 1];
        }
        updatedMappedFiles[id] = newOrder;
      }

      // update the files with the new order if available
      const updatedFiles = fileIds.reduce((updates: UserFilesKeyed, f) => {
        const file = files[f] as UserFileKeyed;
        updates[f] = {
          ...file,
          order:
            updatedMappedFiles[f] != null ? updatedMappedFiles[f] : file.order,
        };

        return updates;
      }, {});

      return {
        ...state,
        files: updatedFiles,
      };
    }
    case Actions.SetFileOrders: {
      const orderUpdates = action.payload;
      const files = state.files;

      const updatedFiles = Object.keys(files).reduce(
        (updates: UserFilesKeyed, f) => {
          const file = files[f] as UserFileKeyed;
          const newOrder = orderUpdates[f];
          if (newOrder != null && file.order !== newOrder) {
            updates[f] = {
              ...file,
              order: newOrder,
            };
          } else {
            updates[f] = file;
          }
          return updates;
        },
        {},
      );

      return {
        ...state,
        files: updatedFiles,
      };
    }
    default:
      return state;
  }
}

function coverPageReducer(
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState {
  switch (action.type) {
    case Actions.UpdateCoverPage: {
      return {
        ...state,
        coverPage: { ...state.coverPage, ...action.payload },
      };
    }
    default:
      return state;
  }
}

function settingsReducer(
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState {
  switch (action.type) {
    case Actions.UpdateSettings: {
      const payload = action.payload;
      return {
        ...state,
        settings: {
          ...state.settings,
          ...payload,
        },
      };
    }
    default:
      return state;
  }
}

function metaReducer(
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState {
  switch (action.type) {
    case Actions.UpdateMeta: {
      const payload = action.payload;
      return {
        ...state,
        meta: {
          ...state.meta,
          ...payload,
        },
      };
    }
    default:
      return state;
  }
}

function userReducer(
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState {
  switch (action.type) {
    case Actions.UpdateUser: {
      const user = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          ...user,
        },
      };
    }
    default:
      return state;
  }
}

const apiAppReducer = (
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState => {
  switch (action.type) {
    case Actions.UpdateApiApp: {
      const apiApp = action.payload;
      return {
        ...state,
        apiApp: {
          ...state.apiApp,
          ...apiApp,
        },
      };
    }
    default:
      return state;
  }
};

const workflowReducer = (
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState => {
  switch (action.type) {
    case Actions.SetWorkflow: {
      return {
        ...state,
        workflow: action.payload,
      };
    }
    default:
      return state;
  }
};

const integrationReducer = (
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState => {
  switch (action.type) {
    case Actions.UpdateIntegrationStatus: {
      const { integration, status } = action.payload;

      // check if the integration is available
      if (
        state.flags.uploadIntegrations &&
        state.flags.uploadIntegrations[integration] == null
      ) {
        return state;
      }

      return {
        ...state,
        flags: {
          ...state.flags,
          uploadIntegrations: {
            ...state.flags.uploadIntegrations,
            [integration]: {
              ...state.flags.uploadIntegrations![integration], // it should be available here
              enabled: status,
            },
          },
        },
      };
    }
    default:
      return state;
  }
};

// The return type helps TypeScript to complain in this file if an action
// returns an invalid shape.
export function prepAndSendReducer(
  state: PrepAndSendNamespaceState = defaultState,
  action: PrepAndSendAction,
): PrepAndSendNamespaceState {
  /* eslint-disable no-param-reassign */
  state = documentReducer(state, action);
  state = recipientReducer(state, action);
  state = filesReducer(state, action);
  state = coverPageReducer(state, action);
  state = settingsReducer(state, action);
  state = metaReducer(state, action);
  state = userReducer(state, action);
  state = apiAppReducer(state, action);
  state = workflowReducer(state, action);
  state = integrationReducer(state, action);
  state = ccReducer(state, action);
  const oldCsvState = state[CSV_EDITOR_KEY];
  const csvState = csvReducer(state[CSV_EDITOR_KEY], action);
  if (oldCsvState !== csvState) {
    state = {
      ...state,
      [CSV_EDITOR_KEY]: csvState,
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  state = reducer(state, action);

  // This can be a useful place to log a subset of the state and an action for
  // debugging.
  return state;
}

function reducer(state: PrepAndSendNamespaceState, action: PrepAndSendAction) {
  switch (action.type) {
    case Actions.Init: {
      const { data, flags, editorFeatures, user, apiApp, embeddedData } =
        action.payload;

      let nextRecipientId = state.meta.nextRecipientId;
      const recipients = data.recipients.map((r) => {
        let id = Number(r.id);
        if (Number.isNaN(id)) {
          id = nextRecipientId;
          r = { ...r, id: String(id) };
        }
        if (id >= nextRecipientId) {
          nextRecipientId = id + 1;
        }

        switch (r.type) {
          case recipientTypes.Signer:
          case recipientTypes.Role:
            return { ...r, attachments: keyBy('id', r.attachments) };

          case recipientTypes.Fax:
            return r;
          default:
            unreachable(r);
            return r;
        }
      });

      const meta = state.meta;
      let workflow = state.workflow;
      let currentPath = meta.currentPath;

      // It should reset if changing to a completely different request.
      if (data.guid !== state.transmissionGroupGuid) {
        currentPath = '';
        workflow = defaultState.workflow;
      }

      return {
        ...defaultState,
        transmissionGroupGuid: data.guid,
        document: data.document,
        bulkSendData: {
          ...defaultState.bulkSendData,
        },
        recipients,
        ccs: data.ccs,
        // files will be updated by additional actions.
        files: {},
        editorFields: data.editorFields,
        coverPage: {
          ...defaultState.coverPage,
          ...data.coverPage,
        },
        user,
        apiApp,
        flags,
        editorFeatures: editorFeatures as Array<string>,
        settings: {
          ...defaultState.settings,
          ...data.settings,
        },
        meta: {
          ...meta,
          currentPath,
          nextRecipientId,
        },
        workflow,
        embeddedData,
      };
    }
    case Actions.UpdateEditorFields: {
      return {
        ...state,
        editorFields: action.payload,
      };
    }
    case Actions.SetFlags: {
      const payload = action.payload;
      return {
        ...state,
        flags: {
          ...state.flags,
          ...payload,
        },
      };
    }
    case Actions.UseTemplate: {
      const data = action.payload;
      return {
        ...state,
        bulkSendData: {
          ...defaultState.bulkSendData,
        },
        templates: [...state.templates, data],
      };
    }
    case Actions.DeleteTemplate: {
      const templateGuid = action.payload;

      return {
        ...state,
        bulkSendData: {
          ...defaultState.bulkSendData,
        },
        templates: state.templates.filter(
          (t) => t.templateGuid !== templateGuid,
        ),
      };
    }
    case Actions.UpdateContacts: {
      return {
        ...state,
        contacts: action.payload,
      };
    }
    case Actions.SetReferAFriendURL: {
      return {
        ...state,
        referAFriendURL: action.payload,
      };
    }
    case Actions.ResetSignerFile: {
      return {
        ...state,
        bulkSendData: {
          ...state.bulkSendData,
          signerFile: {
            ...(defaultState.bulkSendData.signerFile as SignerFile),
          },
        },
      };
    }
    case Actions.SetBulkSendData: {
      return {
        ...state,
        bulkSendData: {
          ...state.bulkSendData,
          ...action.payload,
        },
      };
    }
    case Actions.BulkSendInitialize: {
      return {
        ...state,
        bulkSendData: {
          ...state.bulkSendData,
          dataKey: action.payload,
        },
      };
    }
    case Actions.SetDeepIntegrationError: {
      return {
        ...state,
        deepIntegrationError: {
          ...state.deepIntegrationError,
          ...action.payload,
        },
      };
    }
    case Actions.SetIntegrationError: {
      return {
        ...state,
        integrationError: {
          ...state.integrationError,
          ...action.payload,
        },
      };
    }
    case Actions.SetIntegrationExternalFilename: {
      const filename = action.payload;
      if (state.flags.integration) {
        return {
          ...state,
          flags: {
            ...state.flags,
            integration: {
              name: state.flags.integration.name,
              data: {
                ...state.flags.integration.data,
                externalFilename: filename,
              },
            },
          },
        };
      }
      return state;
    }
    case Actions.SetIntegrationDefaultSavePath: {
      const externalDefaultSavePath = action.payload;
      if (state.flags.integration) {
        return {
          ...state,
          flags: {
            ...state.flags,
            integration: {
              name: state.flags.integration.name,
              data: {
                ...state.flags.integration.data,
                externalDefaultSavePath,
              },
            },
          },
        };
      }
      return state;
    }
    case Actions.UpdateNumberFields: {
      const numFields = action.payload;
      return {
        ...state,
        numFields,
      };
    }
    case Actions.UpdateRequestIsFinished: {
      const requestIsFinished = action.payload;
      return {
        ...state,
        requestIsFinished,
      };
    }
    case Actions.SetPhoneVerificationModalOpen: {
      return {
        ...state,
        phoneVerificationModalOpen: action.payload,
      };
    }
    case Actions.UpdateShouldConvertDocToTemplate: {
      return {
        ...state,
        flags: {
          ...state.flags,
          shouldConvertDocToTemplate: action.payload,
        },
      };
    }
    case Actions.SetSignatureRequestId: {
      return {
        ...state,
        signatureRequestId: action.payload,
      };
    }
    default:
      return state;
  }
}
